import { normalizeIsicId } from './deepLinkParams';
import { pickRegistrationField } from './registrationTableColumns';

let indexPromise = null;

export function loadRcmCaseIndex() {
  if (!indexPromise) {
    const base = process.env.PUBLIC_URL || '';
    indexPromise = fetch(`${base}/rcm_case_index.json`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load case index (${response.status})`);
        }
        return response.json();
      })
      .catch((error) => {
        indexPromise = null;
        throw error;
      });
  }
  return indexPromise;
}

/**
 * Resolve URL params to patient_id / lesion_id using the static index.
 * Returns null when nothing matches. indexMatched is false when IDs came from
 * the URL but were not found in the static case index.
 */
export function resolveDeepLinkTarget(index, params) {
  if (!index || !params) return null;

  let caseKey = null;

  if (params.isicId) {
    caseKey = index.by_isic_id?.[normalizeIsicId(params.isicId)];
    if (caseKey) {
      return entryFromCaseKey(index, caseKey, true);
    }
  }

  if (params.rcmCaseId) {
    caseKey = index.by_rcm_case_id?.[params.rcmCaseId];
    if (caseKey) {
      return entryFromCaseKey(index, caseKey, true);
    }
  }

  if (params.patientId) {
    const patientId = params.patientId;
    const lesionId = params.lesionId;

    if (lesionId) {
      const key = `${patientId}|${lesionId}`;
      if (index.cases?.[key]) {
        return entryFromCaseKey(index, key, true);
      }
      return {
        patientId,
        lesionId,
        rcmCaseId: null,
        indexMatched: false,
      };
    }

    const lesions = index.by_patient_id?.[patientId];
    if (lesions?.length === 1) {
      return entryFromCaseKey(index, `${patientId}|${lesions[0]}`, true);
    }
    if (lesions?.length > 1) {
      return { patientId, lesionId: null, rcmCaseId: null, indexMatched: true };
    }

    return {
      patientId,
      lesionId: null,
      rcmCaseId: null,
      indexMatched: false,
    };
  }

  return null;
}

function entryFromCaseKey(index, caseKey, indexMatched) {
  const entry = index.cases?.[caseKey];
  if (!entry) return null;
  return {
    patientId: entry.patient_id,
    lesionId: entry.lesion_id,
    rcmCaseId: entry.rcm_case_id ?? null,
    indexMatched,
  };
}

export function findPatientFolder(patients, patientId) {
  if (!patientId || !Array.isArray(patients)) return null;
  return (
    patients.find(
      (folder) => folder.name === patientId || folder.meta?.patient_id === patientId
    ) ?? null
  );
}

export function findLesionFolder(lesions, lesionId) {
  if (!lesionId || !Array.isArray(lesions)) return null;
  return (
    lesions.find((folder) => folderMatchesLesionId(folder, lesionId)) ?? null
  );
}

function folderMatchesLesionId(folder, lesionId) {
  if (!folder || !lesionId) return false;
  if (folder.name === lesionId || folder.meta?.lesion_id === lesionId) {
    return true;
  }

  const registrationData = folder.meta?.registrationData;
  if (!Array.isArray(registrationData)) return false;

  return registrationData.some(
    (row) => pickRegistrationField(row, 'lesion_id') === lesionId
  );
}

/** True when lesionId matches registration metadata on a case folder (patient or lesion). */
export function caseFolderMatchesLesionId(caseFolder, lesionId, indexEntry) {
  if (!caseFolder || !lesionId) return false;
  const { lesionId: resolvedLesionId } = getCaseBreadcrumbIds(caseFolder, indexEntry);
  return resolvedLesionId === lesionId;
}

export function resolveCollectionFromParams(params) {
  const value = params?.collection?.toLowerCase();
  if (value === 'isic' || value === 'full') {
    return value;
  }
  return null;
}

export function lookupCaseIndexEntry(index, patientId, lesionId) {
  if (!index?.cases || !patientId) return null;

  if (lesionId) {
    return index.cases[`${patientId}|${lesionId}`] ?? null;
  }

  const patientLesions = index.by_patient_id?.[patientId];
  if (patientLesions?.length === 1) {
    return index.cases[`${patientId}|${patientLesions[0]}`] ?? null;
  }

  return null;
}

export function getCaseBreadcrumbIds(caseFolder, indexEntry) {
  if (!caseFolder) {
    return { lesionId: null, rcmCaseId: null };
  }

  const meta = caseFolder.meta ?? {};
  const registrationData = Array.isArray(meta.registrationData) ? meta.registrationData : [];
  const clinicalRow =
    registrationData.find((row) => pickRegistrationField(row, 'diagnosis')) || registrationData[0];

  const lesionId =
    pickRegistrationField(meta, 'lesion_id')
    || pickRegistrationField(clinicalRow, 'lesion_id')
    || (caseFolder.name?.startsWith('IL_') ? caseFolder.name : null)
    || indexEntry?.lesion_id
    || null;

  const rcmCaseId =
    pickRegistrationField(meta, 'rcm_case_id')
    || pickRegistrationField(clinicalRow, 'rcm_case_id')
    || indexEntry?.rcm_case_id
    || null;

  return { lesionId, rcmCaseId };
}
